import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { loadModules } from 'esri-loader';
// import '@arcgis/core/assets/esri/themes/light/main.css';
import "react-datepicker/dist/react-datepicker.css";
import '../../assets/styles/App.css';
import { FiAlertCircle } from 'react-icons/fi';
import { Alert, Form, Spinner } from 'react-bootstrap';
import api from '../../api/client';
import LandParcelInfoPanel from './LandParcelInfoPanel';
// setDefaultOptions({ version: "4.34", css: true });

const MAX_SELECTED_TILE_LAYERS = 6;
const MAX_SELECTED_ELEVATION_LAYERS = 1;

const IMAGERY_LAYER_LABELS = {
    orthomosaic: 'Orthomosaic',
    ndvi: 'Normalised Difference Vegetation Index',
    dsm: 'Digital Surface Model (3D View only)',
    dtm: 'Digital Terrain Model',
    natural_capital: 'Natural Capital',
    rainfall_simulation: 'Rainfall Simulation',
    twi: 'Topographic Wetness Index',
    land_classification: 'Land Classification',
    flow_direction: 'Flow Direction',
    phosphorus: 'Phosphorus'
};
// const SIDE_PANEL_DEFAULT_WIDTH = 380;
// const SIDE_PANEL_MIN_WIDTH = 340;
// const SIDE_PANEL_MAX_WIDTH = 920;

function MapViewComponent() {
  /**
   * Refs to store ArcGIS map views and shared map instance
   */
  const mapViewRef = useRef(null); // Stores and allows access to the 2D MapView object of the map
  const sceneViewRef = useRef(null); // Stores and allows access to the 3D SceneView object of the map
  const mapRef = useRef(null); // Stores the map object (base map layers, etc) shared between 2D and 3D views
  const mapInstanceRef = useRef(null); // Stores the ArcGIS map instance so selected imagery layers can be added later
  const activeViewRef = useRef(null); // Keeps the latest active view available to ArcGIS callbacks
  const selectedGraphicRef = useRef(null); // Keeps selected map graphic available without re-registering click handlers
  const selectedSiteIdRef = useRef(''); // Keeps latest selected site available to ArcGIS callbacks without rebuilding the map
  const graphicsByParcelIdRef = useRef({ map: {}, scene: {} }); // Lookup table for selecting polygons from dropdowns
  const showPolygonsRef = useRef(true); // Lets ArcGIS click handlers read the latest polygon visibility state
  const autoSelectedParcelSiteRef = useRef(null); // Tracks one-parcel site auto-selection so users can deselect it manually
  const imageryGroupLayerRef = useRef(null); // Stores the dynamic imagery group shown in the LayerList
  const imageryElevationLayersRef = useRef([]); // Tracks dynamic elevation layers added to map ground
  const arcgisLayerConstructorsRef = useRef({ TileLayer: null, ElevationLayer: null }); // Stores constructors loaded by esri-loader

  /**
   * State Variables
   */
  const [activeView, setActiveView] = useState(null); // Holds the map view object (MapView for 2D and SceneView for 3D)
  const [viewType, setViewType] = useState('2d'); // Track current map view as a string (2D or 3D)
  const [selectedGraphic, setSelectedGraphic] = useState(null); // Track selected polygon on map
  const [showPolygons, setShowPolygons] = useState(true); // Track visibility of polygons (visible on default)
  const [isSidePanelCollapsed, setIsSidePanelCollapsed] = useState(false); // Track whether the side panel is hidden to give the map more room
  const [isSelectorPanelCollapsed, setIsSelectorPanelCollapsed] = useState(true); // Track whether the floating selector panel is collapsed over the map
  const [mapReady, setMapReady] = useState(false); // Track when ArcGIS map and layer constructors are ready
  const [landData, setLandData] = useState([]); // Store nested project, site and land parcel data from FastAPI
  const [loadingLandData, setLoadingLandData] = useState(true); // Track loading state for land data request
  const [landDataError, setLandDataError] = useState(null); // Track land data request errors
  const [selectedProjectId, setSelectedProjectId] = useState(''); // Track project dropdown selection
  const [selectedSiteId, setSelectedSiteId] = useState(''); // Track site dropdown selection
  const [selectedParcelId, setSelectedParcelId] = useState(''); // Track land parcel dropdown selection
  const [imageryCategories, setImageryCategories] = useState([]); // Track imagery category buttons for the selected site
  const [selectedImageryCategory, setSelectedImageryCategory] = useState(''); // Track active imagery category button
  const [selectedMissionId, setSelectedMissionId] = useState(''); // Track selected flight date/acquisition mission
  const [selectedTileLayers, setSelectedTileLayers] = useState([]); // Track selected tile layers shown in the ArcGIS grouped LayerList
  const [selectedElevationLayer, setSelectedElevationLayer] = useState(null); // Track the single selected DSM/elevation layer for 3D ground
  const [loadingImageryCategories, setLoadingImageryCategories] = useState(false); // Track category request loading state
  const [imageryCategoryError, setImageryCategoryError] = useState(null); // Track category request errors
  const [imageryData, setImageryData] = useState(null); // Store imagery missions and layers for the selected site
  const [loadingImageryData, setLoadingImageryData] = useState(false); // Track imagery request loading state
  const [imageryDataError, setImageryDataError] = useState(null); // Track imagery request errors
  // const [sidePanelWidth, setSidePanelWidth] = useState(SIDE_PANEL_DEFAULT_WIDTH);

  useEffect(() => {
    activeViewRef.current = activeView;
  }, [activeView]);

  useEffect(() => {
    selectedGraphicRef.current = selectedGraphic;
  }, [selectedGraphic]);

  useEffect(() => {
    selectedSiteIdRef.current = selectedSiteId;
  }, [selectedSiteId]);

  useEffect(() => {
    showPolygonsRef.current = showPolygons;
  }, [showPolygons]);

  /**
   * Fetches all land data when the map page loads.
   */
  useEffect(() => {
    let isMounted = true;

    const fetchLandData = async () => {
      setLoadingLandData(true);
      setLandDataError(null);

      try {
        const response = await api.get('/api/v1/land-management/land-data');
        if (isMounted) {
          setLandData(Array.isArray(response.data) ? response.data : []);
        }
      } catch (requestError) {
        if (isMounted) {
          setLandData([]);
          setLandDataError(requestError);
        }
      } finally {
        if (isMounted) {
          setLoadingLandData(false);
        }
      }
    };

    fetchLandData();

    return () => {
      isMounted = false;
    };
  }, []);

  const selectedProject = useMemo(
    () => landData.find((project) => project.project_id === selectedProjectId) || null,
    [landData, selectedProjectId]
  );

  const selectedSite = useMemo(
    () => selectedProject?.sites?.find((site) => site.site_id === selectedSiteId) || null,
    [selectedProject, selectedSiteId]
  );

  const availableSites = useMemo(
    () => selectedProject?.sites || [],
    [selectedProject]
  );

  const availableLandParcels = useMemo(
    () => selectedSite?.land_parcels || [],
    [selectedSite]
  );

  const selectedLandParcel = useMemo(
    () => availableLandParcels.find((parcel) => parcel.land_parcel_guid === selectedParcelId) || null,
    [availableLandParcels, selectedParcelId]
  );

  const imageryCategoriesFromData = useMemo(() => {
    const missions = imageryData?.acquisition_missions || [];
    return [...new Set(missions.map((mission) => mission.source_type).filter(Boolean))];
  }, [imageryData]);

  const selectedMission = useMemo(() => {
    const missions = imageryData?.acquisition_missions || [];

    return missions.find((mission) => (
      mission.mission_id === selectedMissionId &&
      (!selectedImageryCategory || mission.source_type === selectedImageryCategory)
    )) || null;
  }, [imageryData, selectedImageryCategory, selectedMissionId]);

  const sortedProjects = useMemo(() => (
    [...landData].sort((projectA, projectB) => (
      (projectA.project_name || '').localeCompare(projectB.project_name || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    ))
  ), [landData]);

  const sortedSites = useMemo(() => (
    [...availableSites].sort((siteA, siteB) => (
      (siteA.site_name || '').localeCompare(siteB.site_name || '', undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    ))
  ), [availableSites]);

  const sortedLandParcels = useMemo(() => (
    [...availableLandParcels].sort((parcelA, parcelB) => (
      String(parcelA.land_parcel_number || '').localeCompare(String(parcelB.land_parcel_number || ''), undefined, {
        numeric: true,
        sensitivity: 'base',
      })
    ))
  ), [availableLandParcels]);

  const getProjectParcelIds = useCallback((project) => (
    project?.sites?.flatMap((site) => site.land_parcels?.map((parcel) => parcel.land_parcel_guid) || []) || []
  ), []);

  const getSiteParcelIds = useCallback((site) => (
    site?.land_parcels?.map((parcel) => parcel.land_parcel_guid) || []
  ), []);

  const formatShortDate = useCallback((dateValue) => {
    if (!dateValue) return 'No date';

    const parsedDate = new Date(dateValue);
    if (Number.isNaN(parsedDate.getTime())) return dateValue;

    return parsedDate.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: '2-digit',
    });
  }, []);

  const formatImageryCategoryLabel = useCallback((category) => {
    if (!category) return 'Imagery';

    const normalisedCategory = category.toLowerCase();
    if (normalisedCategory === 'lidar') return 'LiDAR';

    return category.charAt(0).toUpperCase() + category.slice(1);
  }, []);

  const getImageryLayerKey = useCallback((mission, imageryLayer) => (
    [
      mission?.mission_id || 'mission',
      imageryLayer?.agol_url || 'url',
      imageryLayer?.platform_title || imageryLayer?.agol_title || 'title',
      imageryLayer?.layer_type || 'layer',
      imageryLayer?.agol_layer_type || 'agol',
    ].join('|')
  ), []);

  const isElevationImageryLayer = useCallback((imageryLayer) => {
    const agolLayerType = (imageryLayer?.agol_layer_type || '').toLowerCase();
    const layerType = (imageryLayer?.layer_type || '').toLowerCase();

    return agolLayerType.includes('elevation') || layerType.includes('dsm') || layerType.includes('elevation');
  }, []);

  const isTileImageryLayer = useCallback((imageryLayer) => {
    const agolLayerType = (imageryLayer?.agol_layer_type || '').toLowerCase();
    const layerType = (imageryLayer?.layer_type || '').toLowerCase();

    return !isElevationImageryLayer(imageryLayer) && (agolLayerType.includes('tile') || layerType.includes('tile'));
  }, [isElevationImageryLayer]);

  const buildSelectedImageryLayer = useCallback((mission, imageryLayer) => ({
    key: getImageryLayerKey(mission, imageryLayer),
    mission_id: mission?.mission_id,
    category: mission?.source_type,
    acquired_at_start: mission?.acquired_at_start,
    acquired_at_end: mission?.acquired_at_end,
    agol_url: imageryLayer?.agol_url,
    agol_layer_type: imageryLayer?.agol_layer_type,
    agol_title: imageryLayer?.agol_title,
    layer_type: imageryLayer?.layer_type,
    platform_title: imageryLayer?.platform_title,
  }), [getImageryLayerKey]);

  const getSelectedLayerTitle = useCallback((selectedLayer, isLayerSelection) => {
    const title = isLayerSelection
    ? IMAGERY_LAYER_LABELS[selectedLayer.layer_type] || selectedLayer.platform_title || selectedLayer.agol_title || 'Imagery layer'
    : selectedLayer.platform_title || selectedLayer.agol_title || selectedLayer.layer_type || 'Imagery layer';
    const date = formatShortDate(selectedLayer.acquired_at_end || selectedLayer.acquired_at_start);
    const category = formatImageryCategoryLabel(selectedLayer.category);

    return `${title} | ${date} | ${category}`;
  }, [formatImageryCategoryLabel, formatShortDate]);

  const selectedTileLayerKeys = useMemo(
    () => new Set(selectedTileLayers.map((layer) => layer.key)),
    [selectedTileLayers]
  );

  /**
   * Retrieves the opposite view (2D or 3D) based on the current active view
   * @param {Object} view - The currently active view
   * @returns {Object} - The other view (2D if current is 3D, and vice versa)
   */
  const getOtherView = useCallback((view) => (view === mapViewRef.current ? sceneViewRef.current : mapViewRef.current), []);

  const getGraphicLookupForView = useCallback((view) => {
    if (view === sceneViewRef.current) {
      return graphicsByParcelIdRef.current.scene;
    }

    return graphicsByParcelIdRef.current.map;
  }, []);

  const getGraphicForParcel = useCallback((view, parcelId) => {
    if (!view || !parcelId) return null;
    return getGraphicLookupForView(view)?.[parcelId] || null;
  }, [getGraphicLookupForView]);

  const getDefaultSymbol = useCallback((view, isSelected) => (
    view?.type === '3d'
      ? {
          type: 'polygon-3d',
          symbolLayers: [
            {
              type: 'fill',
              material: {
                color: isSelected ? [255, 20, 147, 0.01] : [0, 0, 255, 0.01], // Transparent color when not selected
              },
              outline: {
                color: isSelected ? [255, 20, 147] : [0, 0, 255], // Outline color
                size: 3,
              },
            },
          ],
        }
      : {
          type: 'simple-fill',
          color: [0, 0, 0, 0], // Transparent fill in 2D
          outline: {
            color: isSelected ? [255, 20, 147] : [0, 0, 255], // Outline color
            width: 3,
          },
        }
  ), []);

  /**
   * Updates the symbol of a selected or deselected graphic
   * @param {Object} view - The view where the graphic is being updated
   * @param {Object} graphic - The graphic being updated
   * @param {boolean} isSelected - Whether the graphic is selected (true) or deselected (false)
   */
  const updateGraphicSymbol = useCallback((view, graphic, isSelected) => {
    if (!view || !graphic) return;
    graphic.symbol = getDefaultSymbol(view, isSelected);
  }, [getDefaultSymbol]);

  const updateParcelSelectionSymbol = useCallback((parcelId, isSelected) => {
    [mapViewRef.current, sceneViewRef.current].forEach((view) => {
      const graphic = getGraphicForParcel(view, parcelId);
      updateGraphicSymbol(view, graphic, isSelected);
    });
  }, [getGraphicForParcel, updateGraphicSymbol]);

  /**
   * Deselects the currently selected graphic in both 2D and 3D views.
   * Resets the symbol and clears the selection state.
   */
  const deselectGraphic = useCallback(() => {
    const currentGraphic = selectedGraphicRef.current;

    if (currentGraphic) {
      updateParcelSelectionSymbol(currentGraphic.attributes?.LandParcelId, false);
      selectedGraphicRef.current = null;
      setSelectedGraphic(null);
      setSelectedParcelId('');
    }
  }, [updateParcelSelectionSymbol]);

  const goToGraphics = useCallback((parcelIds) => {
    const ids = Array.isArray(parcelIds) ? parcelIds : [parcelIds];
    const zoomViews = [mapViewRef.current, sceneViewRef.current].filter(Boolean);

    zoomViews.forEach((view) => {
      const graphics = ids
        .map((parcelId) => getGraphicForParcel(view, parcelId))
        .filter((graphic) => {
          const extent = graphic?.geometry?.extent;

          // Ignore broken polygon extents that would zoom far beyond a land parcel/site/project.
          return (
            extent &&
            extent.width < 1 &&
            extent.height < 1
          );
        });

      if (!graphics.length) return;

      view.goTo({
        target: graphics,
        padding: {
          top: 60,
          right: 60,
          bottom: 60,
          left: 60,
        },
      }).catch((goToError) => {
        console.error('Error zooming to land parcel polygons:', goToError);
      });
    });
  }, [getGraphicForParcel]);

  /**
   * Clears the selected imagery layer builder state when the selected site changes.
   */
  const clearSelectedImagerySelection = useCallback(() => {
    setSelectedTileLayers([]);
    setSelectedElevationLayer(null);
  }, []);

  /**
   * Selects a graphic in the specified view and synchronizes the selection with the other view.
   * Highlights the selected graphic and ensures only one graphic is selected at a time.
   *
   * @param {Object} graphic - The graphic to be selected.
   * @param {Object} view - The map view (2D or 3D) where the selection occurred.
   */
  const selectGraphic = useCallback((graphic, view, shouldZoom = false) => {
    if (!graphic || !view) return;

    const parcelId = graphic.attributes?.LandParcelId;
    const nextSiteId = graphic.attributes?.SiteId || '';
    const previousParcelId = selectedGraphicRef.current?.attributes?.LandParcelId;

    if (selectedSiteIdRef.current && selectedSiteIdRef.current !== nextSiteId) {
      clearSelectedImagerySelection();
    }

    if (previousParcelId && previousParcelId !== parcelId) {
      updateParcelSelectionSymbol(previousParcelId, false); // Reset the previous selection before selecting a new one
    }

    selectedGraphicRef.current = graphic;
    setSelectedGraphic(graphic);
    setSelectedProjectId(graphic.attributes?.ProjectId || '');
    setSelectedSiteId(nextSiteId);
    setSelectedParcelId(parcelId || '');

    // Update the selected graphic symbol in the current view (highlight in pink)
    updateGraphicSymbol(view, graphic, true);

    // Update the selected graphic symbol in the other view (highlight in pink)
    updateGraphicSymbol(getOtherView(view), getGraphicForParcel(getOtherView(view), parcelId), true);

    if (shouldZoom && parcelId) {
      goToGraphics(parcelId);
    }
  }, [clearSelectedImagerySelection, getGraphicForParcel, getOtherView, goToGraphics, updateGraphicSymbol, updateParcelSelectionSymbol]);

  /**
   * Selects the only available project, site or land parcel when the user has no alternative choices.
   */
  useEffect(() => {
    if (loadingLandData || landDataError || !landData.length) return;

    if (selectedParcelId && mapReady) {
      const selectedParcelGraphic = getGraphicForParcel(activeViewRef.current, selectedParcelId) || getGraphicForParcel(mapViewRef.current, selectedParcelId);
      const currentSelectedParcelId = selectedGraphicRef.current?.attributes?.LandParcelId;

      if (selectedParcelGraphic && currentSelectedParcelId !== selectedParcelId) {
        selectGraphic(selectedParcelGraphic, activeViewRef.current || mapViewRef.current, true);
        return;
      }
    }

    if (!selectedProjectId && landData.length === 1) {
      const onlyProject = landData[0];
      const onlyProjectSites = onlyProject.sites || [];
      const onlySite = onlyProjectSites.length === 1 ? onlyProjectSites[0] : null;
      const onlySiteParcels = onlySite?.land_parcels || [];
      const parcelIds = getProjectParcelIds(onlyProject);

      setSelectedProjectId(onlyProject.project_id);

      if (onlySite) {
        setSelectedSiteId(onlySite.site_id);
      }

      if (onlySiteParcels.length === 1) {
        autoSelectedParcelSiteRef.current = onlySite.site_id;
        setSelectedParcelId(onlySiteParcels[0].land_parcel_guid);
      } else if (mapReady && parcelIds.length) {
        goToGraphics(parcelIds);
      }

      return;
    }

    if (selectedProject && !selectedSiteId && availableSites.length === 1) {
      const onlySite = availableSites[0];
      const onlySiteParcels = onlySite.land_parcels || [];
      const parcelIds = getSiteParcelIds(onlySite);

      setSelectedSiteId(onlySite.site_id);

      if (onlySiteParcels.length === 1) {
        autoSelectedParcelSiteRef.current = onlySite.site_id;
        setSelectedParcelId(onlySiteParcels[0].land_parcel_guid);
      } else if (mapReady && parcelIds.length) {
        goToGraphics(parcelIds);
      }

      return;
    }

    if (
      selectedSite &&
      !selectedParcelId &&
      availableLandParcels.length === 1 &&
      autoSelectedParcelSiteRef.current !== selectedSiteId
    ) {
      // Auto-select a single land parcel only once per site so clicking off the map can clear it.
      autoSelectedParcelSiteRef.current = selectedSiteId;
      setSelectedParcelId(availableLandParcels[0].land_parcel_guid);
    }
  }, [
    availableLandParcels,
    availableSites,
    getGraphicForParcel,
    getProjectParcelIds,
    getSiteParcelIds,
    goToGraphics,
    landData,
    landDataError,
    loadingLandData,
    mapReady,
    selectGraphic,
    selectedParcelId,
    selectedProject,
    selectedProjectId,
    selectedSite,
    selectedSiteId,
  ]);

  /**
   * Toggles the visibility of polygons on the map.
   * Updates the `showPolygons` state and modifies the visibility of graphics in the
   * active view (2D or 3D).
  */
  const togglePolygonVisibility = () => {
    setShowPolygons((prev) => !prev);
  };

  /**
   * Toggles the side panel so the map can use the full available workspace.
   */
  const toggleSidePanel = () => {
    setIsSidePanelCollapsed((prev) => !prev);
  };

  /**
   * Toggles the floating project/site/parcel selector panel over the map.
   */
  const toggleSelectorPanel = () => {
    setIsSelectorPanelCollapsed((prev) => !prev);
  };

  /**
   * Clears previously selected imagery mission layers from the map.
   */
  const clearSelectedImageryLayers = useCallback(() => {
    const imageryGroupLayer = imageryGroupLayerRef.current;

    imageryGroupLayer?.layers?.removeAll();

    // Hide the selected imagery group until there is a real selected layer to show.
    if (mapInstanceRef.current && imageryGroupLayer && mapInstanceRef.current.layers.find((layer) => layer === imageryGroupLayer)) {
      mapInstanceRef.current.remove(imageryGroupLayer);
    }

    imageryElevationLayersRef.current.forEach((layer) => {
      mapInstanceRef.current?.ground?.layers?.remove(layer);
    });
    imageryElevationLayersRef.current = [];
  }, []);

  const handleAddImageryLayer = useCallback((mission, imageryLayer) => {
    if (!mission || !imageryLayer?.agol_url) return;

    const selectedLayer = buildSelectedImageryLayer(mission, imageryLayer);

    if (isElevationImageryLayer(imageryLayer)) {
      if (!selectedElevationLayer || selectedElevationLayer.key === selectedLayer.key) {
        setSelectedElevationLayer(selectedLayer);
      }
      return;
    }

    if (!isTileImageryLayer(imageryLayer)) return;

    setSelectedTileLayers((currentLayers) => {
      if (currentLayers.some((layer) => layer.key === selectedLayer.key)) return currentLayers;
      if (currentLayers.length >= MAX_SELECTED_TILE_LAYERS) return currentLayers;

      return [...currentLayers, selectedLayer];
    });
  }, [buildSelectedImageryLayer, isElevationImageryLayer, isTileImageryLayer, selectedElevationLayer]);

  const handleAddAllMissionLayers = useCallback((mission) => {
    if (!mission?.imagery_layers?.length) return;

    const tileLayersToAdd = mission.imagery_layers
      .filter((imageryLayer) => imageryLayer?.agol_url && isTileImageryLayer(imageryLayer))
      .map((imageryLayer) => buildSelectedImageryLayer(mission, imageryLayer));

    setSelectedTileLayers((currentLayers) => {
      const existingKeys = new Set(currentLayers.map((layer) => layer.key));
      const availableSlots = MAX_SELECTED_TILE_LAYERS - currentLayers.length;
      const uniqueLayersToAdd = tileLayersToAdd
        .filter((layer) => !existingKeys.has(layer.key))
        .slice(0, availableSlots);

      return [...currentLayers, ...uniqueLayersToAdd];
    });

    if (!selectedElevationLayer) {
      const elevationLayer = mission.imagery_layers.find((imageryLayer) => (
        imageryLayer?.agol_url && isElevationImageryLayer(imageryLayer)
      ));

      if (elevationLayer) {
        setSelectedElevationLayer(buildSelectedImageryLayer(mission, elevationLayer));
      }
    }
  }, [buildSelectedImageryLayer, isElevationImageryLayer, isTileImageryLayer, selectedElevationLayer]);

  const handleRemoveSelectedTileLayer = useCallback((layerKey) => {
    setSelectedTileLayers((currentLayers) => currentLayers.filter((layer) => layer.key !== layerKey));
  }, []);

  const handleRemoveSelectedElevationLayer = useCallback(() => {
    setSelectedElevationLayer(null);
  }, []);

  const handleClearAllSelectedImageryLayers = useCallback(() => {
    clearSelectedImagerySelection();
  }, [clearSelectedImagerySelection]);

  /**
   * useEffect hook to update polygon visibility whenever `showPolygons` or `activeView` changes.
   * Clones each graphic to apply the updated visibility, removes all existing graphics,
   * and re-adds the modified graphics to the active view with updated visibility.
   */
  useEffect(() => {
    [mapViewRef.current, sceneViewRef.current].forEach((view) => {
      if (!view) return;

      // Iterate over all graphics and update their visibility
      view.graphics.forEach((graphic) => {
        graphic.visible = showPolygons;
      });
    });
  }, [showPolygons, activeView]);

  /**
   * Loads imagery categories for the selected site.
   */
  useEffect(() => {
    let isMounted = true;

    const fetchImageryCategories = async () => {
      if (!selectedSiteId) {
        setImageryCategories([]);
        setSelectedImageryCategory('');
        setSelectedMissionId('');
        setImageryCategoryError(null);
        return;
      }

      setLoadingImageryCategories(true);
      setImageryCategoryError(null);
      setImageryCategories([]);
      setSelectedImageryCategory('');
      setSelectedMissionId('');

      try {
        const response = await api.get(`/api/v1/imagery/sites/${selectedSiteId}/imagery-categories`);
        const categories = Array.isArray(response.data) ? response.data : [];

        if (isMounted) {
          setImageryCategories(categories);
          if (categories.length === 1) {
            setSelectedImageryCategory(categories[0]);
          }
        }
      } catch (requestError) {
        if (isMounted) {
          setImageryCategoryError(requestError);
        }
      } finally {
        if (isMounted) {
          setLoadingImageryCategories(false);
        }
      }
    };

    fetchImageryCategories();

    return () => {
      isMounted = false;
    };
  }, [selectedSiteId]);

  /**
   * Loads imagery missions for the selected site.
   */
  useEffect(() => {
    let isMounted = true;

    const fetchImageryData = async () => {
      if (!selectedSiteId) {
        setImageryData(null);
        setImageryDataError(null);
        setSelectedMissionId('');
        return;
      }

      setLoadingImageryData(true);
      setImageryDataError(null);
      setSelectedMissionId('');

      try {
        const response = await api.get(`/api/v1/imagery/imagery-data/${selectedSiteId}`);
        if (isMounted) {
          setImageryData(response.data);
        }
      } catch (requestError) {
        if (isMounted) {
          setImageryData(null);
          setImageryDataError(requestError);
        }
      } finally {
        if (isMounted) {
          setLoadingImageryData(false);
        }
      }
    };

    fetchImageryData();

    return () => {
      isMounted = false;
    };
  }, [selectedSiteId]);

  /**
   * Uses imagery data as a fallback category source if the category endpoint returns no categories.
   */
  useEffect(() => {
    if (imageryCategories.length || !imageryCategoriesFromData.length) return;

    setImageryCategories(imageryCategoriesFromData);
    if (imageryCategoriesFromData.length === 1) {
      setSelectedImageryCategory(imageryCategoriesFromData[0]);
    }
  }, [imageryCategories.length, imageryCategoriesFromData]);

  /**
   * Automatically selects the latest flight date for a newly selected imagery category.
   */
  useEffect(() => {
    if (!selectedImageryCategory || selectedMissionId) return;

    const matchingMissions = (imageryData?.acquisition_missions || [])
      .filter((mission) => mission.source_type === selectedImageryCategory);

    if (!matchingMissions.length) return;

    const latestMission = [...matchingMissions].sort((missionA, missionB) => {
      const missionADate = new Date(missionA.acquired_at_end || missionA.acquired_at_start).getTime();
      const missionBDate = new Date(missionB.acquired_at_end || missionB.acquired_at_start).getTime();

      return (Number.isNaN(missionBDate) ? 0 : missionBDate) - (Number.isNaN(missionADate) ? 0 : missionADate);
    })[0];

    if (latestMission?.mission_id) {
      setSelectedMissionId(latestMission.mission_id);
    }
  }, [imageryData, selectedImageryCategory, selectedMissionId]);

  /**
   * Loads selected custom imagery layers into the ArcGIS layer lists.
   */
  useEffect(() => {
    clearSelectedImageryLayers();

    if (!mapReady) return;

    const { TileLayer, ElevationLayer } = arcgisLayerConstructorsRef.current;
    if (!TileLayer || !ElevationLayer) return;

    selectedTileLayers.forEach((selectedLayer) => {
      if (!selectedLayer.agol_url) return;

      const tileLayer = new TileLayer({
        url: selectedLayer.agol_url,
        title: getSelectedLayerTitle(selectedLayer, false),
        listMode: "hide-children",
        preloadEnabled: true,
        visible: false,
      });

      // Only add the group layer once it contains selected imagery layers.
      if (mapInstanceRef.current && imageryGroupLayerRef.current && !mapInstanceRef.current.layers.find((layer) => layer === imageryGroupLayerRef.current)) {
        mapInstanceRef.current.add(imageryGroupLayerRef.current);
      }

      imageryGroupLayerRef.current?.layers?.add(tileLayer);
    });

    if (selectedElevationLayer?.agol_url) {
      const elevationLayer = new ElevationLayer({
        url: selectedElevationLayer.agol_url,
        title: getSelectedLayerTitle(selectedElevationLayer, false),
        visible: true,
      });

      mapInstanceRef.current?.ground?.when(() => {
        mapInstanceRef.current?.ground?.layers?.add(elevationLayer);
      });
      imageryElevationLayersRef.current.push(elevationLayer);
    }
  }, [clearSelectedImageryLayers, getSelectedLayerTitle, mapReady, selectedElevationLayer, selectedTileLayers]);

  const buildRingsFromPolygon = useCallback((polygon) => {
    if (!polygon) return null;

    if (polygon.type === 'Polygon') {
      return polygon.coordinates;
    }

    if (polygon.type === 'MultiPolygon') {
      return polygon.coordinates.flat();
    }

    return null;
  }, []);

  /**
   * - Loads all the modules required for ArcGIS JS SDK. Defines the map and 2D & 3D views.
   * - Defines and adds elevation & imagery layers for both views.
   * - Retrieves land data from backend.
   * - Defines and adds to map all land parcel graphics/polygons based on retrieved data from backend.
   */
  useEffect(() => {
    if (loadingLandData || landDataError || !landData) {
      // If the data is still loading or there is an error, do not proceed with map setup
      return undefined;
    }

    let isCancelled = false;
    let mapViewInstance = null;
    let sceneViewInstance = null;
    const clickHandles = [];

    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/views/SceneView',
      'esri/widgets/LayerList',
      'esri/layers/GroupLayer',
      'esri/layers/TileLayer',
      'esri/layers/ElevationLayer',
      'esri/Graphic',
      'esri/geometry/Polygon',
      'esri/geometry/geometryEngine',
      'esri/widgets/ScaleBar',
      'esri/widgets/Compass',
      'esri/config',
    ])
    .then(([
      Map, MapView, SceneView,
      LayerList, GroupLayer, TileLayer, ElevationLayer,
      Graphic, Polygon, geometryEngine, ScaleBar, Compass,
      esriConfig,
    ]) => {

      if (isCancelled) {
        return;
      }

      // esri ArcGIS API Key for imagery access
      // esriConfig.apiKey = process.env.REACT_APP_COMMERCIAL_ARCGIS_API_KEY;
      esriConfig.apiKey = window.__APP_CONFIG__?.PUBLIC_REACT_APP_COMMERCIAL_ARCGIS_API_KEY;
      // esriConfig.apiKey = process.env.REACT_APP_EDU_ARCGIS_API_KEY

      // Mapping constants
      const DEFAULT_MAP_CENTRE_POINT = [-6.660, 54.678]; // Roughly centre of NI
      const DEFAULT_MAP_ZOOM = 9; // zoom out to see all of NI

      const map = new Map({
        basemap: 'hybrid', // Real-world satellite imagery
        ground: 'world-elevation',
      });
      mapInstanceRef.current = map;
      arcgisLayerConstructorsRef.current = { TileLayer, ElevationLayer };

      // Group layer for imagery selected from site/category/flight date controls
      const selectedImageryGroupLayer = new GroupLayer({
        title: 'Selected Imagery Layers',
        layers: [],
        visible: true,
      });
      imageryGroupLayerRef.current = selectedImageryGroupLayer;

      // Create the 2D MapView
      mapViewInstance = new MapView({
        container: mapRef.current,
        map: map,
        center: DEFAULT_MAP_CENTRE_POINT,
        zoom: DEFAULT_MAP_ZOOM,
      });
      mapViewRef.current = mapViewInstance;

      // Create the 3D SceneView (with no container initially)
      sceneViewInstance = new SceneView({
        container: null,
        map: map,
        center: DEFAULT_MAP_CENTRE_POINT,
        zoom: DEFAULT_MAP_ZOOM,
      });
      sceneViewRef.current = sceneViewInstance;

      mapViewInstance.container.style.setProperty('--esri-view-outline-color', 'transparent');
      sceneViewInstance.container?.style?.setProperty('--esri-view-outline-color', 'transparent');

      // Add the ScaleBar widget to 2D view (unavailable in 3D)
      mapViewInstance.ui.add(
        new ScaleBar({ view: mapViewInstance, unit: 'metric' }),
        'bottom-left'
      );

      // Add the Compass widget to 2D view
      mapViewInstance.ui.add(
        new Compass({ view: mapViewInstance}),
        'top-left'
      );

      // Add the LayerList widget to 2D view
      const layerList2D = new LayerList({
        view: mapViewInstance, // Your MapView or SceneView
        listItemCreatedFunction: (event) => {
          const item = event.item;

          // Add legend only for non-group layers
          if (item.layer.type !== "group") {
            item.panel = {
              content: "legend", // Embed the legend for the layer
              open: false,       // Close the legend by default
            };
          }
        },
        dragEnabled: true, // enables reordering
        // selectionMode: "single-persist",
      });

      // Add LayerList with embedded legend to the view
      mapViewInstance.ui.add(layerList2D, "top-right");

      // Add the LayerList widget to 3D view
      const layerList3D = new LayerList({
        view: sceneViewInstance,
        listItemCreatedFunction: (event) => {
          const item = event.item;

          // Add legend only for non-group layers
          if (item.layer.type !== "group") {
            item.panel = {
              content: "legend", // Embed the legend for the layer
              open: false, // Open the legend by default
            };
          }
        },
        dragEnabled: true, // enables reordering
      });
      sceneViewInstance.ui.add(layerList3D, 'top-right');

      // Add polygons from landData
      const graphics = [];
      const mapGraphicsByParcelId = {};
      const sceneGraphicsByParcelId = {};

      // Loop through each project, site and land parcel that has a polygon
      landData.forEach((project) => {
        project.sites?.forEach((site) => {
          site.land_parcels?.forEach((landParcel) => {
            const rings = buildRingsFromPolygon(landParcel.polygon);
            if (!rings) return;

            // Create polygon from GeoJSON data returned by FastAPI
            const polygon = geometryEngine.simplify(new Polygon({
              rings,
              spatialReference: { wkid: 4326 },
            }));

            if (!polygon) return;

            // Define data to be displayed for each land parcel
            const landParcelAttributes = {
              ProjectId: project.project_id,
              Project: project.project_name,
              SiteId: site.site_id,
              Site: site.site_name,
              LandParcelId: landParcel.land_parcel_guid,
              Name: `${site.site_name}, Parcel ${landParcel.land_parcel_number || ''}`.trim(),
              County: site.county || 'Unavailable',
              Size: landParcel.area_in_ha ? parseFloat(landParcel.area_in_ha).toFixed(3) : 'Unavailable',
              CropType: landParcel.crop_type || 'Unavailable',
              Notes: landParcel.notes || '',
            };

            // Define polygon with data
            const mapGraphic = new Graphic({
              geometry: polygon,
              attributes: landParcelAttributes,
              visible: showPolygonsRef.current,
              symbol: getDefaultSymbol(mapViewInstance, false),
            });
            const sceneGraphic = mapGraphic.clone();
            sceneGraphic.symbol = getDefaultSymbol(sceneViewInstance, false);

            // Add graphics/polygons to both 2D and 3D view
            mapViewInstance.graphics.add(mapGraphic);
            sceneViewInstance.graphics.add(sceneGraphic);
            graphics.push(mapGraphic);
            mapGraphicsByParcelId[landParcel.land_parcel_guid] = mapGraphic;
            sceneGraphicsByParcelId[landParcel.land_parcel_guid] = sceneGraphic;
          });
        });
      });

      graphicsByParcelIdRef.current = {
        map: mapGraphicsByParcelId,
        scene: sceneGraphicsByParcelId,
      };

      // Auto-zoom to show all polygons
      if (graphics.length > 0) {
        mapViewInstance.when(() => {
          // Compute combined extent
          const combinedExtent = graphics
            .map(g => g.geometry.extent)
            .reduce((acc, ext) => (acc ? acc.union(ext) : ext), null)
            .expand(1.1); // padding

          // Go to extent
          mapViewInstance.goTo(combinedExtent).catch(console.error);
          sceneViewInstance.goTo(combinedExtent).catch(console.error);
        });
      }

      setActiveView(mapViewInstance);
      setMapReady(true);

      /**
       * Handles click events on the specified map view.
       * Determines if a graphic was clicked and selects/deselects accordingly.
       *
       * @param {Object} view - The map view (2D or 3D) to attach the event listener to.
       */
      const handleClick = (view) => view.on("click", (event) => {
        if (!showPolygonsRef.current) return;

        view.hitTest(event).then((response) => {
          // find the clicked polygon
          const graphic = response.results.find((result) => result.graphic?.attributes?.LandParcelId)?.graphic;
          if (graphic) {
            selectGraphic(graphic, view);
          } else {
            // If no graphic is clicked, deselect all graphics
            deselectGraphic();
          }
        });
      });

      clickHandles.push(handleClick(mapViewInstance));
      clickHandles.push(handleClick(sceneViewInstance));
    })
    .catch((err) => {
      console.error('Error loading modules:', err);
    });

    return () => {
      isCancelled = true;
      setMapReady(false);
      clearSelectedImageryLayers();
      clickHandles.forEach((handle) => handle.remove()); // Remove listeners
      selectedGraphicRef.current = null;
      graphicsByParcelIdRef.current = { map: {}, scene: {} };
      imageryGroupLayerRef.current = null;
      mapInstanceRef.current = null;
      arcgisLayerConstructorsRef.current = { TileLayer: null, ElevationLayer: null };
      if (mapViewInstance) mapViewInstance.destroy();
      if (sceneViewInstance) sceneViewInstance.destroy();
    };
  }, [buildRingsFromPolygon, clearSelectedImageryLayers, deselectGraphic, getDefaultSymbol, landData, loadingLandData, landDataError, selectGraphic]);

  const handleImageryCategorySelect = (category) => {
    setSelectedImageryCategory(category);
    setSelectedMissionId('');
  };

  const handleProjectChange = (event) => {
    const projectId = event.target.value;
    const project = landData.find((projectItem) => projectItem.project_id === projectId);
    const parcelIds = getProjectParcelIds(project);

    autoSelectedParcelSiteRef.current = null;
    deselectGraphic();
    clearSelectedImagerySelection();
    setSelectedProjectId(projectId);
    setSelectedSiteId('');
    setSelectedParcelId('');
    setSelectedImageryCategory('');
    setSelectedMissionId('');

    if (parcelIds.length) {
      goToGraphics(parcelIds);
    }
  };

  const handleSiteChange = (event) => {
    const siteId = event.target.value;
    const site = selectedProject?.sites?.find((siteItem) => siteItem.site_id === siteId);
    const parcelIds = getSiteParcelIds(site);

    autoSelectedParcelSiteRef.current = null;
    deselectGraphic();
    clearSelectedImagerySelection();
    setSelectedSiteId(siteId);
    setSelectedParcelId('');
    setSelectedImageryCategory('');
    setSelectedMissionId('');

    if (parcelIds.length) {
      goToGraphics(parcelIds);
    }
  };

  const handleParcelChange = (event) => {
    const parcelId = event.target.value;
    setSelectedParcelId(parcelId);

    if (!parcelId) {
      deselectGraphic();
      return;
    }

    const graphic = getGraphicForParcel(activeViewRef.current, parcelId) || getGraphicForParcel(mapViewRef.current, parcelId);
    if (graphic) {
      selectGraphic(graphic, activeViewRef.current || mapViewRef.current, true);
    }
  };

  /**
   * Switches between 2D (MapView) and 3D (SceneView) while maintaining the current viewpoint.
   * Adjusts the viewpoint scale appropriately when transitioning between views.
   */
  const switchView = () => {
    if (!activeView) return;

    // Check whether current view is 3D, if not it is 2D
    const is3D = activeView.type === '3d';
    // Clone the current viewpoint (camera position, extent, etc.)
    const activeViewpoint = activeView.viewpoint.clone();

    // Define constant for converting degrees to radians
    const DEGREES_TO_RADIANS = Math.PI / 180.0;

    // Get the latitude of the current view's center for scale adjustment
    const latitude = activeView.center.latitude;
    // Calculate the scale conversion factor by using cosine of the latitude in radians
    const scaleConversionFactor = Math.cos(latitude * DEGREES_TO_RADIANS);

    // Detach the current view from the container to switch views
    activeView.container = null;

    // Converting from 3D to 2D:
    if (is3D) {
      // Adjust the viewpoint scale for the 2D projection
      activeViewpoint.scale /= scaleConversionFactor;

      // Set the MapView with the updated viewpoint and attach it to the container
      mapViewRef.current.viewpoint = activeViewpoint;
      mapViewRef.current.container = mapRef.current;

      // Update the active view and view type to 2D
      setActiveView(mapViewRef.current);
      setViewType('2d');
    } else { // Converting from 2D to 3D:
      // Adjust the viewpoint scale for the 3D projection
      activeViewpoint.scale *= scaleConversionFactor;

      // Set the SceneView with the updated viewpoint and attach it to the container
      sceneViewRef.current.viewpoint = activeViewpoint;
      sceneViewRef.current.container = mapRef.current;
      sceneViewRef.current.container.style.setProperty('--esri-view-outline-color', 'transparent');

      // Update the active view and view type to 3D
      setActiveView(sceneViewRef.current);
      setViewType('3d');
    }
  };

  // const handleSidePanelResizeStart = useCallback((event) => {
  //   event.preventDefault();
  //
  //   const startX = event.clientX;
  //   const startWidth = sidePanelWidth;
  //
  //   const handleMouseMove = (moveEvent) => {
  //     const deltaX = startX - moveEvent.clientX;
  //     const nextWidth = Math.min(
  //       SIDE_PANEL_MAX_WIDTH,
  //       Math.max(SIDE_PANEL_MIN_WIDTH, startWidth + deltaX)
  //     );
  //
  //     setSidePanelWidth(nextWidth);
  //   };
  //
  //   const handleMouseUp = () => {
  //     window.removeEventListener('mousemove', handleMouseMove);
  //     window.removeEventListener('mouseup', handleMouseUp);
  //   };
  //
  //   window.addEventListener('mousemove', handleMouseMove);
  //   window.addEventListener('mouseup', handleMouseUp);
  // }, [sidePanelWidth]);

  return (
    <div className="land-map-page">
      {landDataError && (
        <Alert className="land-map-alert" variant="danger">
          <FiAlertCircle size={22} />
          <span>Land data could not be loaded. Please try again.</span>
        </Alert>
      )}

      <div className={`land-map-workspace ${isSidePanelCollapsed ? 'land-map-workspace-panel-hidden' : ''}`}>
        <div className="land-map-view-shell">
          {loadingLandData && (
            <div className="land-map-loading-overlay">
              <Spinner animation="border" role="status" />
              <span>Loading land parcels...</span>
            </div>
          )}

          {/* The map container */}
          <div ref={mapRef} className="map-container land-map-container"></div>

          {/* Floating selector panel keeps project, site and land parcel controls inside the map area. */}
          <section className={`land-map-selector-panel ${isSelectorPanelCollapsed ? 'land-map-selector-panel-collapsed' : ''}`}>
            <div className="land-map-selector-header">
              <div>
                <p className="land-map-selector-eyebrow">Map Filters</p>
                <h3>{selectedSite?.site_name || selectedProject?.project_name || 'Land Selection'}</h3>
              </div>
              {/* Collapse selector panel button */}
              <calcite-button
                className="esri-widget--button land-map-selector-toggle"
                title={isSelectorPanelCollapsed ? 'Show Map Filters' : 'Hide Map Filters'}
                alignment="center"
                appearance="outline-fill"
                kind="neutral"
                scale="m"
                split-child="false"
                type="button"
                width="auto"
                icon-start={isSelectorPanelCollapsed ? 'filter' : 'chevrons-left'}
                onClick={toggleSelectorPanel}
              />
            </div>

            {!isSelectorPanelCollapsed && (
              <div className="land-map-selector-fields">
                <Form.Group className="land-map-filter" controlId="project-select">
                  <Form.Label>Project</Form.Label>
                  <Form.Select
                    value={selectedProjectId}
                    onChange={handleProjectChange}
                    disabled={loadingLandData || !landData.length}
                  >
                    <option value="">Select Project</option>
                    {sortedProjects.map((project) => (
                      <option key={project.project_id} value={project.project_id}>
                        {project.project_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="land-map-filter" controlId="site-select">
                  <Form.Label>Site</Form.Label>
                  <Form.Select
                    value={selectedSiteId}
                    onChange={handleSiteChange}
                    disabled={!selectedProjectId || !availableSites.length}
                  >
                    <option value="">Select Site</option>
                    {sortedSites.map((site) => (
                      <option key={site.site_id} value={site.site_id}>
                        {site.site_name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                <Form.Group className="land-map-filter" controlId="land-parcel-select">
                  <Form.Label>Land Parcel</Form.Label>
                  <Form.Select
                    value={selectedParcelId}
                    onChange={handleParcelChange}
                    disabled={!selectedSiteId || !availableLandParcels.length || !showPolygons}
                  >
                    <option value="">Select Land Parcel</option>
                    {sortedLandParcels.map((landParcel) => (
                      <option key={landParcel.land_parcel_guid} value={landParcel.land_parcel_guid}>
                        {landParcel.land_parcel_number}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
            )}
          </section>

          {/* Display the map control to change between views, hide/show polygons and expand the map */}
          <div className="map-controls-container land-map-controls-container">
            {/* Change between 2D (MapView) and 3D (SceneView) button */}
            <calcite-button
                className="esri-widget--button"
                title={viewType === '2d' ? 'Switch to 3D' : 'Switch to 2D'}
                alignment="center"
                appearance="outline-fill"
                kind="neutral"
                scale="m"
                split-child="false"
                type="button"
                width="auto"
                icon-start={viewType === '2d' ? '3D' : '2D'}
                onClick={switchView}
            />

            {/* Hide/show polygons button */}
            <calcite-button
                className="esri-widget--button"
                title={showPolygons ? 'Hide Polygons' : 'Show Polygons'}
                alignment="center"
                appearance="outline-fill"
                kind="neutral"
                scale="m"
                split-child="false"
                type="button"
                width="auto"
                icon-start={showPolygons ? 'layer-hide' : 'layer'}
                onClick={togglePolygonVisibility}
            />

            {/* Hide/show side panel button */}
            <calcite-button
                className="esri-widget--button"
                title={isSidePanelCollapsed ? 'Show Details Panel' : 'Hide Details Panel'}
                alignment="center"
                appearance="outline-fill"
                kind="neutral"
                scale="m"
                split-child="false"
                type="button"
                width="auto"
                icon-start={isSidePanelCollapsed ? 'chevrons-left' : 'chevrons-right'}
                onClick={toggleSidePanel}
            />
          </div>
        </div>

        {!isSidePanelCollapsed && (
          <LandParcelInfoPanel
            selectedProject={selectedProject}
            selectedSite={selectedSite}
            selectedLandParcel={selectedLandParcel}
            imageryCategories={imageryCategories}
            selectedImageryCategory={selectedImageryCategory}
            setSelectedImageryCategory={handleImageryCategorySelect}
            selectedMissionId={selectedMissionId}
            setSelectedMissionId={setSelectedMissionId}
            selectedTileLayers={selectedTileLayers}
            selectedTileLayerKeys={selectedTileLayerKeys}
            selectedElevationLayer={selectedElevationLayer}
            maxSelectedTileLayers={MAX_SELECTED_TILE_LAYERS}
            maxSelectedElevationLayers={MAX_SELECTED_ELEVATION_LAYERS}
            onAddImageryLayer={handleAddImageryLayer}
            onAddAllMissionLayers={handleAddAllMissionLayers}
            onRemoveSelectedTileLayer={handleRemoveSelectedTileLayer}
            onRemoveSelectedElevationLayer={handleRemoveSelectedElevationLayer}
            onClearSelectedImageryLayers={handleClearAllSelectedImageryLayers}
            getImageryLayerKey={getImageryLayerKey}
            isElevationImageryLayer={isElevationImageryLayer}
            isTileImageryLayer={isTileImageryLayer}
            getSelectedLayerTitle={getSelectedLayerTitle}
            loadingImageryCategories={loadingImageryCategories}
            imageryCategoryError={imageryCategoryError}
            imageryData={imageryData}
            loadingImageryData={loadingImageryData}
            imageryDataError={imageryDataError}
            imageryLayerLabel={IMAGERY_LAYER_LABELS}
            // onResizeStart={handleSidePanelResizeStart}
          />
        )}
      </div>
    </div>
  );
}

export default MapViewComponent;
